import React, { useState, useEffect, useRef } from "react";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import fetchWithJWT from "../api/fetchWithJWT";
import PlanBadge from "../components/PlanBadge";
import paymentManagementService from "../services/paymentManagementService";
import profileService from "../services/profileService";
import {
  subscribeToNotifications,
  readNotification,
  readAllNotifications,
  notificationPath,
  timeAgo,
} from "../services/sellerNotificationService";
import inventoryNotificationService from "../services/inventoryNotificationService";
import { shippingStatus, SHIPPING_SETTINGS_PATH, SHIPPING_CHANGED_EVENT } from "../utils/shippingStatus";
import { ShippingPill, ShippingBanner, ShippingNavBadge } from "../components/ShippingSetup";
// import logo from '../img/logo.png';

// How long opening an order waits for its notification to be marked read
const MARK_READ_WAIT_MS = 1500;

const NavigationBar = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [signup, setSignup] = useState("");
  const [businessData, setBusinessData] = useState(null);
  const [tooltipText, setTooltipText] = useState("");
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState('');

  const [savedStoreUrl, setSavedStoreUrl] = useState("");
  const isMobile = window.innerWidth <= 768;
  const sidebarRef = useRef(null);

  // Plan Upgrade states
  const [planInfo, setPlanInfo] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // Track subscription status separately
  
  // Payment Management states
  const [showManualPayButton, setShowManualPayButton] = useState(true);
  const [showSubscriptionButton, setShowSubscriptionButton] = useState(true);
  const [paymentType, setPaymentType] = useState(null);

  // Profile Completion Notification states
  const [profileComplete, setProfileComplete] = useState(true);
  const [missingFields, setMissingFields] = useState([]);
  const [showProfileNotification, setShowProfileNotification] = useState(false);
  const profilePollingRef = useRef(null);

  // Order notifications from the API ("New order received"): the unread ones
  // listed in the bell, and how many are unread in all. Kept across pages by
  // services/sellerNotificationService.js
  const [orderNotifications, setOrderNotifications] = useState([]);
  const [unreadOrderCount, setUnreadOrderCount] = useState(0);

  // Inventory Notification states
  const [stockAlerts, setStockAlerts] = useState([]);
  const [criticalStockCount, setCriticalStockCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);

  useEffect(() => {
    const url = localStorage.getItem("storeUrl");
    if (url) {
      setSavedStoreUrl(url);   // update state → triggers re-render
    }
  }, []); // runs once after component loads

  useEffect(() => {
    const businessId = localStorage.getItem("businessId");
    if (businessId) {
      fetchStoreInfo(businessId);
    }
  }, []);

  useEffect(() => {
    // Fetch plan info and payment management on component mount
    const business_id = localStorage.getItem("business_id");
    if (business_id) {
      fetchPlanInfo(business_id);
      fetchPaymentManagement(business_id);
      startProfilePolling(business_id);
      startInventoryPolling(business_id);
    }
    const stopOrderPolling = business_id ? subscribeToNotifications(handleOrderNotifications) : null;

    // Cleanup polling on unmount
    return () => {
      if (profilePollingRef.current) {
        profileService.stopProfilePolling(profilePollingRef.current);
      }
      if (stopOrderPolling) {
        stopOrderPolling();
      }
      if (business_id) {
        inventoryNotificationService.stopInventoryPolling(business_id);
      }
      // Cleanup notification-open class to prevent permanent scroll lock
      document.body.classList.remove('notification-open');
      document.body.style.top = '';
    };
  }, []);
  
  // Start real-time profile polling
  const startProfilePolling = (businessId) => {
    // Stop any existing polling
    if (profilePollingRef.current) {
      profileService.stopProfilePolling(profilePollingRef.current);
    }

    // Start new polling with 3 second interval
    profilePollingRef.current = profileService.startProfilePolling(
      businessId,
      (profileCheck) => {
        setProfileComplete(profileCheck.isComplete);
        setMissingFields(profileCheck.missingFields);
        console.log('[NavigationBar] Profile status updated:', profileCheck);
      },
      2000 // Poll every 2 seconds
    );
  };

  // The bell's order notifications (services/sellerNotificationService.js).
  // A toast for the ones never shown before (by id; none on the page's first check)
  const handleOrderNotifications = ({ notifications, unreadCount, added }) => {
    setOrderNotifications(notifications);
    setUnreadOrderCount(unreadCount);
    if (added.length > 0) {
      const count = added.length;
      toast.info(`🎉 ${count} new order${count > 1 ? 's' : ''} received! Add the tracking details to dispatch.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Closes the bell's dropdown (and, on mobile, gives the page its scroll back)
  const closeNotifications = () => {
    setShowProfileNotification(false);
    if (isMobile) {
      const scrollY = parseInt(document.body.style.top || '0') * -1;
      document.body.classList.remove('notification-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    }
  };

  // A new order: gone from the bell at once, marked read on the server, then
  // its page in Order Management opens. The page's own bell checks the server
  // as it opens, so it waits for the mark (briefly, review L-2)
  const openOrderNotification = async (notification) => {
    closeNotifications();
    try {
      await Promise.race([
        readNotification(notification),
        new Promise(resolve => setTimeout(resolve, MARK_READ_WAIT_MS)),
      ]);
    } catch (error) {
      console.error('[NavigationBar] Error marking notification read:', error);
    }
    navigate(notificationPath(notification) || '/ordermanagement');
  };

  const markAllOrdersRead = () => {
    readAllNotifications().catch(error =>
      console.error('[NavigationBar] Error marking notifications read:', error)
    );
  };

  // Start real-time inventory polling
  const startInventoryPolling = (businessId) => {
    // Stop any existing polling
    inventoryNotificationService.stopInventoryPolling(businessId);

    // Start new polling with 30 second interval
    inventoryNotificationService.startInventoryPolling(
      businessId,
      (stockInfo) => {
        setStockAlerts(stockInfo.alerts);
        setCriticalStockCount(stockInfo.criticalCount);
        setLowStockCount(stockInfo.warningCount);
        console.log('[NavigationBar] Stock alerts updated:', stockInfo);
        
        // Show toast notification for new critical alerts
        if (stockInfo.newAlerts && stockInfo.newAlerts.length > 0) {
          const criticalAlerts = stockInfo.newAlerts.filter(a => a.severity === 'critical');
          if (criticalAlerts.length > 0) {
            toast.warning(`⚠️ ${criticalAlerts.length} product${criticalAlerts.length > 1 ? 's' : ''} critically low on stock!`, {
              position: "top-right",
              autoClose: 7000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          }
        }
      },
      30000 // Poll every 30 seconds
    );
  };
  
  // Fetch payment management info to control button visibility
  const fetchPaymentManagement = async (businessId) => {
    try {
      const buttonVisibility = await paymentManagementService.getNavigationButtonVisibility(businessId);
      setShowManualPayButton(buttonVisibility.showManualPayButton);
      setShowSubscriptionButton(buttonVisibility.showSubscriptionButton);
      setPaymentType(buttonVisibility.paymentType);
      console.log('[NavigationBar] Payment management:', buttonVisibility);
    } catch (error) {
      console.error('[NavigationBar] Error fetching payment management:', error);
    }
  };


  useEffect(() => {
    // Retrieve email address from localStorage
    const email_address = window.localStorage.getItem("email_address");
    const phone_number = window.localStorage.getItem("phone_number");
    const signup = window.localStorage.getItem("signup");
    const business_id = window.localStorage.getItem("business_id");

    // Update the state with the retrieved email address
    setEmail(phone_number);
    setSignup(signup);
    if (signup === "user" && business_id) {
      fetchBusinessData(business_id);
    }
  }, [email, signup]);

  // Saved on the Shipping Settings page: the pill, banner and badge follow at once
  useEffect(() => {
    const refresh = () => {
      const business_id = window.localStorage.getItem("business_id");
      if (window.localStorage.getItem("signup") === "user" && business_id) fetchBusinessData(business_id);
    };
    window.addEventListener(SHIPPING_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(SHIPPING_CHANGED_EVENT, refresh);
  }, []);

  useEffect(() => {
  const handleClickOutside = (event) => {
    // Check if sidebar is open and click is outside sidebar and menu button
    const isMenuButton = event.target.closest('[data-widget="pushmenu"]');
    const isSidebarClick = sidebarRef.current?.contains(event.target);
    
    if (
      document.body.classList.contains("sidebar-open") &&
      !isSidebarClick &&
      !isMenuButton
    ) {
      document.body.classList.remove("sidebar-open");
      document.body.classList.add("sidebar-collapse");
    }

    // Close notification dropdown when clicking outside
    const notificationBell = event.target.closest('.nav-item.dropdown');
    if (showProfileNotification && !notificationBell) {
      setShowProfileNotification(false);
      if (isMobile) {
        // Restore scroll position when closing via outside click
        const scrollY = parseInt(document.body.style.top || '0') * -1;
        document.body.classList.remove('notification-open');
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      }
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, [showProfileNotification]);

  // useEffect(() => {
  //   // Override AdminLTE pushmenu to prevent auto-close
  //   const body = document.body;

  //   const handleBodyClick = (e) => {
  //     // Don't close sidebar on body clicks
  //     if (body.classList.contains('sidebar-open')) {
  //       e.stopPropagation();
  //     }
  //   };

  //   // Prevent pushmenu from closing on outside click
  //   document.addEventListener('click', handleBodyClick, true);

  //   return () => {
  //     document.removeEventListener('click', handleBodyClick, true);
  //   };
  // }, []);

  useEffect(() => {
  const handleClickOutside = (event) => {
    // Check if sidebar is open and click is outside sidebar and menu button
    const isMenuButton = event.target.closest('[data-widget="pushmenu"]');
    const isSidebarClick = sidebarRef.current?.contains(event.target);
    
    if (
      document.body.classList.contains("sidebar-open") &&
      !isSidebarClick &&
      !isMenuButton
    ) {
      document.body.classList.remove("sidebar-open");
      document.body.classList.add("sidebar-collapse");
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  const fetchBusinessData = async (businessId) => {
    try {
      const response = await fetchWithJWT(`/business/store/info?business_id=${businessId}`);
      const data = await response.json();
      if (data.status === "success") {
        setBusinessData(data.result);
      } else {
        setBusinessData({ has_web_store: false, store_status: null });
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
      setBusinessData({ has_web_store: false, store_status: null });
    }
  };

  const [storeData, setStoreData] = useState({
    business_name: '',
    store_subdomain: '',
    store_status: 'inactive',
    store_settings: {
      logo_url: '',
      primary_color: '#007bff',
      secondary_color: '#6c757d',
      header_color: '#fff',  // Add this
      footer_color: '#fff',
      accent_color: '#030303ff',
      font_family: 'Arial, sans-serif',
      store_description: '',
      contact_email: '',
      contact_phone: '',
      contact_whatsapp: '',
      social_links: {
        facebook: '',
        instagram: '',
        twitter: ''
      },
      seo_settings: {
        meta_title: '',
        meta_description: '',
        meta_keywords: ''
      },
      banner_section: {
        enabled: false,
        size: 'medium',
        slides: []
      },
      category_section: {
        enabled: false,
        title: 'Shop by Categories',
        selected_categories: []
      },
      featured_section: {
        enabled: false,
        title: 'Featured Products',
        selected_products: []
      }
    }
  });

  useEffect(() => {
    const business_id = localStorage.getItem('business_id');
    if (business_id) {
      setBusinessId(business_id);
      fetchStoreInfo(business_id);

    }
  }, []);

 const updateFetchStoreInfo = (data) => {
  const result = data.result || {};

  const apiSettings = result.store_settings || {};

  setStoreData(prev => ({
    ...prev,
    business_name: result.business_name || "",
    store_subdomain: result.store_subdomain || "",
    store_status: result.store_status || "inactive",

    store_settings: {
      ...prev.store_settings,
      ...apiSettings,

      social_links: {
        ...prev.store_settings.social_links,
        ...(apiSettings.social_links || {})
      },

      seo_settings: {
        ...prev.store_settings.seo_settings,
        ...(apiSettings.seo_settings || {})
      },

      banner_section: {
        ...prev.store_settings.banner_section,
        ...(apiSettings.banner_section || {})
      },

      category_section: {
        ...prev.store_settings.category_section,
        ...(apiSettings.category_section || {})
      },

      featured_section: {
        ...prev.store_settings.featured_section,
        ...(apiSettings.featured_section || {})
      }
    }
  }));

  // Save Store URL
  if (result.store_subdomain) {
    const finalUrl = `https://${result.store_subdomain}.mytamilkart.com`;
    localStorage.setItem("store_url", finalUrl);
  }
};



  const fetchStoreInfo = async (businessId) => {
    try {
      setLoading(true);
      const response = await fetchWithJWT(`/business/store/info?business_id=${businessId}`);
      const data = await response.json();

      if (data.status === 'success') {
        // Use the updateFetchStoreInfo function to properly set all data
        updateFetchStoreInfo(data);
      } else {
        toast.error('Failed to load store information');
      }
    } catch (error) {
      console.error('Error fetching store info:', error);
      toast.error('Error loading store information');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanInfo = async (businessId) => {
    try {
      setLoadingPlan(true);
      const response = await fetchWithJWT(`/business/plan/info?business_user_id=${businessId}`);
      const data = await response.json();

      if (data.status === "success" && data.result) {
        const result = data.result;
        
        // Check if user has an active plan (not expired, not "none", not "Unknown Plan", not "created")
        if (result.plan_name && 
            result.plan_name.toLowerCase() !== 'unknown plan' &&
            result.plan_status !== "none" && 
            result.plan_status !== "created" &&
            !result.is_expired) {
          setPlanInfo({
            plan_name: result.plan_name,
            plan_id: result.plan_id,
            expires_on: result.expires_on,
            plan_status: result.plan_status,
            is_expired: result.is_expired
          });
        } else {
          // No plan, expired, "Unknown Plan", or status is "none" or "created"
          setPlanInfo(null);
        }
      } else {
        // No plan or free plan
        setPlanInfo(null);
      }

      // Fetch subscription status separately
      await fetchSubscriptionStatus(businessId);
    } catch (error) {
      console.error("Error fetching plan info:", error);
      setPlanInfo(null);
    } finally {
      setLoadingPlan(false);
    }
  };

  const fetchSubscriptionStatus = async (businessId) => {
    try {
      const response = await fetchWithJWT(`/subscription/info?business_user_id=${businessId}`);
      const data = await response.json();

      if (data.status === "success" && data.result && data.result.has_subscription) {
        setSubscriptionStatus({
          status: data.result.status,
          subscription_id: data.result.subscription_id,
          paid_count: data.result.paid_count,
          first_payment_discount_amount: data.result.first_payment_discount_amount || 0
        });
      } else {
        setSubscriptionStatus(null);
      }
    } catch (error) {
      console.error("Error fetching subscription status:", error);
      setSubscriptionStatus(null);
    }
  };

  const handlePlanUpgrade = () => {
    const business_id = localStorage.getItem("business_id");
    if (business_id) {
      // Navigate to plans page in same window
      navigate('/purchase-plan');
    } else {
      toast.error("Business ID not found. Please login again.");
    }
  };

  const createWebStore = async () => {
    const business_id = window.localStorage.getItem("business_id");

    if (!business_id) {
      alert(t('navigation.businessIdNotFound'));
      return;
    }

    try {
      const response = await fetchWithJWT('/business/store/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ business_id })
      });

      const data = await response.json();

      if (data.status === "success") {
        alert(t('navigation.webStoreCreated'));
        // Refresh business data to show the new store
        fetchBusinessData(business_id);
      } else {
        alert(data.error?.message || t('navigation.webStoreCreationFailed'));
      }
    } catch (error) {
      console.error("Error creating web store:", error);
    }
  };
  function logout() {
    const confirmLogout = window.confirm("Are you sure you want to exit?");
    if (confirmLogout) {
      localStorage.clear();
      window.location.href = "/login"; // Redirect to login page
    }
    // If user clicks "Cancel", nothing happens and stays on the same page
  }

  const handleProfile = (data) => {
    // Handle null, undefined, or empty data
    if (!data || typeof data !== 'string') {
      return '';
    }

    let displayName;

    if (data.length > 20) {
      const prefix = data.substring(0, 4); // Get the first 4 characters
      const domain = data.substring(data.indexOf("@")); // Get the domain part starting from '@'
      displayName = prefix + "....." + domain;
    } else {
      displayName = data;
    }
    return displayName;
  };
  const handleMouseOver = () => {
    setTooltipText(email);
  };

  const handleMouseOut = () => {
    setTooltipText("");
  };
  // The seller's plans, as the Pay Manually / Auto-Renew buttons show them: colour,
  // ACTIVE / INACTIVE badge and the collapsed sidebar's ✓ / ! (one place, so they agree)
  const hasManualPlan = !!(planInfo && planInfo.plan_name && planInfo.plan_name.toLowerCase() !== 'unknown' && planInfo.plan_name.toLowerCase() !== 'unknown plan' && planInfo.plan_status !== 'cancelled' && planInfo.plan_status !== 'created');
  const hasAutoRenew = !!((subscriptionStatus?.paid_count > 0 || subscriptionStatus?.first_payment_discount_amount > 0) && subscriptionStatus?.status !== 'cancelled');
  const path = window.location.pathname;
  // The store's shipping setup (utils/shippingStatus.js): "unknown", so nothing
  // shows, until the store info says
  const shipping = signup === "user" ? shippingStatus(businessData) : { state: "unknown" };
  return (
    <div>
      {/* Navbar */}
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        {/* Left navbar links */}
        <ul className="navbar-nav">
          <li className="nav-item">
            <a
              className="nav-link"
      data-widget="pushmenu"
      href="#"
      role="button"
      onClick={(e) => {
        const isMobileView = window.innerWidth <= 991;
        
        // Only prevent default and handle manually on mobile
        if (isMobileView) {
          e.preventDefault();
          e.stopPropagation();
          
          const body = document.body;
          const sidebar = document.querySelector('.main-sidebar');
          
          // Check if sidebar is currently open
          const isOpen = body.classList.contains("sidebar-open") || 
                         (sidebar && sidebar.style.marginLeft === '0px');
          
          if (isOpen) {
            // Close sidebar
            body.classList.remove("sidebar-open");
            body.classList.add("sidebar-collapse");
            if (sidebar) sidebar.style.marginLeft = '-250px';
          } else {
            // Open sidebar
            body.classList.remove("sidebar-collapse");
            body.classList.add("sidebar-open");
            if (sidebar) sidebar.style.marginLeft = '0';
          }
        }
        // On desktop, let AdminLTE's data-widget="pushmenu" handle it automatically
      }}
    >
      <i className="fas fa-bars" />
  </a>
</li>
        </ul>


{/* Desktop View */}
{!isMobile && (
  <span className="d-block f_black" style={{ display: "block" }}>
    Store Link :
    <a
      href={`https://${storeData.store_subdomain}.mytamilkart.com`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        marginLeft: "6px",
        color: "var(--g, #1E5B3A)",
        
      }}
    >
      {storeData.store_subdomain
        ? `${storeData.store_subdomain}.mytamilkart.com`
        : `${storeData.store_subdomain}.mytamilkart.com`}
    </a>
  </span>
)}
{!isMobile && <ShippingPill status={shipping} />}

{/* Mobile + Tablet View */}
{isMobile && (
  <h4 style={{ display: "block" }}>
    <a
      href={`https://${storeData.store_subdomain}.mytamilkart.com`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "var(--g, #1E5B3A)",
        
      }}
    >
      Store Link
    </a>
  </h4>
)}



   
   
        {/* Right navbar links */}
        <ul className="navbar-nav ml-auto">
         
          
          {/* Plan Badge - Shows current plan status (Desktop only) */}
          {/* COMMENTED OUT - Don't show plan badge for users without active plan/subscription */}
          {/* {signup === "user" && !isMobile && (
            <li className="nav-item" style={{ display: 'flex', alignItems: 'center', marginRight: '10px' }}>
              <PlanBadge />
            </li>
          )} */}

          {/* Unified Notification Bell - Profile + Orders + Inventory */}
          {signup === "user" && (
            <li className="nav-item dropdown" style={{ position: 'relative', marginRight: '8px' }}>
              <div
                className="nav-link"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProfileNotification(!showProfileNotification);
                  
                  // Prevent body scroll on mobile when dropdown is open
                  if (isMobile) {
                    if (!showProfileNotification) {
                      // Save current scroll position before locking
                      const scrollY = window.scrollY;
                      document.body.style.top = `-${scrollY}px`;
                      document.body.classList.add('notification-open');
                    } else {
                      // Restore scroll position when closing
                      const scrollY = parseInt(document.body.style.top || '0') * -1;
                      document.body.classList.remove('notification-open');
                      document.body.style.top = '';
                      window.scrollTo(0, scrollY);
                    }
                  }
                }}
                style={{ 
                  cursor: 'pointer', 
                  position: 'relative',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Bell Icon Container */}
                <div style={{
                  position: 'relative',
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: (!profileComplete || criticalStockCount > 0 || lowStockCount > 0 || unreadOrderCount > 0) 
                    ? 'var(--gs, #E1EFE3)'
                    : 'var(--sf, #fff)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: (!profileComplete || criticalStockCount > 0 || lowStockCount > 0 || unreadOrderCount > 0)
                    ? '0 2px 8px rgba(30, 91, 58, 0.15)'
                    : 'inset 0 0 0 1px var(--line, #E7DCC6)'
                }}>
                  <i className="fa-solid fa-bell" style={{ 
                    fontSize: '18px', 
                    color: (!profileComplete || criticalStockCount > 0 || lowStockCount > 0 || unreadOrderCount > 0) ? 'var(--g, #1E5B3A)' : '#9e9e9e',
                    animation: (!profileComplete || criticalStockCount > 0 || lowStockCount > 0 || unreadOrderCount > 0) ? 'bellShake 2s ease-in-out infinite' : 'none'
                  }}></i>
                  
                  {/* Notification Badge: red for critical stock, green for new orders, else amber */}
                  {(criticalStockCount > 0 || lowStockCount > 0 || unreadOrderCount > 0) ? (
                    <span data-testid="notification-badge" style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      minWidth: '20px',
                      height: '20px',
                      background: criticalStockCount > 0 
                        ? 'linear-gradient(135deg, #ff4757 0%, #dc3545 100%)'
                        : unreadOrderCount > 0
                          ? 'linear-gradient(135deg, #2E7D4F 0%, #1E5B3A 100%)'
                          : 'linear-gradient(135deg, #F0854A 0%, #D9640E 100%)',
                      borderRadius: '10px',
                      border: '2px solid white',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 5px',
                      animation: 'pulse 2s ease-in-out infinite',
                      boxShadow: criticalStockCount > 0
                        ? '0 2px 8px rgba(220, 53, 69, 0.4)'
                        : unreadOrderCount > 0
                          ? '0 2px 8px rgba(40, 167, 69, 0.4)'
                          : '0 2px 8px rgba(255, 152, 0, 0.4)',
                      letterSpacing: '-0.5px'
                    }}>
                      {(() => {
                        const total = criticalStockCount + lowStockCount + unreadOrderCount;
                        return total > 99 ? '99+' : total;
                      })()}
                    </span>
                  ) : !profileComplete && (
                    <span style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '12px',
                      height: '12px',
                      background: 'linear-gradient(135deg, #F0854A 0%, #D9640E 100%)',
                      borderRadius: '50%',
                      border: '2px solid white',
                      animation: 'pulse 2s ease-in-out infinite',
                      boxShadow: '0 2px 6px rgba(255, 152, 0, 0.4)'
                    }}></span>
                  )}
                </div>

                {showProfileNotification && (
                  <>
                    {/* Mobile backdrop overlay */}
                    {isMobile && (
                      <div
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'rgba(0, 0, 0, 0.3)',
                          zIndex: 1050,
                          backdropFilter: 'blur(2px)'
                        }}
                        onClick={() => {
                          setShowProfileNotification(false);
                          // Restore scroll position when closing via backdrop
                          const scrollY = parseInt(document.body.style.top || '0') * -1;
                          document.body.classList.remove('notification-open');
                          document.body.style.top = '';
                          window.scrollTo(0, scrollY);
                        }}
                      />
                    )}
                    
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="notification-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        background: '#ffffff',
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                        zIndex: 1060,
                        minWidth: isMobile ? '320px' : '380px',
                        maxWidth: isMobile ? '95vw' : '420px',
                        marginTop: '12px',
                        overflow: 'hidden',
                        // Mobile specific positioning
                        ...(isMobile && {
                          position: 'fixed',
                          top: '60px',
                          right: '10px',
                          left: '10px',
                          minWidth: 'auto',
                          maxWidth: 'none',
                          width: 'calc(100vw - 20px)',
                          marginTop: '0'
                        })
                      }}
                    >
                    {/* Header */}
                    <div style={{
                      padding: isMobile ? '16px 20px' : '20px 24px',
                      background: 'var(--g, #1E5B3A)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: isMobile ? '32px' : '36px',
                          height: isMobile ? '32px' : '36px',
                          borderRadius: '10px',
                          background: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <i className="fa-solid fa-bell" style={{ 
                            color: 'white', 
                            fontSize: isMobile ? '16px' : '18px'
                          }}></i>
                        </div>
                        <div>
                          <h6 style={{ 
                            margin: 0, 
                            color: 'white', 
                            fontSize: isMobile ? '14px' : '16px', 
                            fontWeight: 600,
                            letterSpacing: '-0.2px'
                          }}>
                            Notifications
                          </h6>
                          <p style={{
                            margin: 0,
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 400
                          }}>
                            {(() => {
                              const total = unreadOrderCount + criticalStockCount + lowStockCount + (!profileComplete ? 1 : 0);
                              return total > 0 ? `${total} ${total === 1 ? 'update' : 'updates'}` : 'All caught up';
                            })()}
                          </p>
                        </div>
                      </div>
                      {/* Close button for mobile */}
                      {isMobile && (
                        <button
                          onClick={() => {
                            setShowProfileNotification(false);
                            // Restore scroll position when closing via close button
                            const scrollY = parseInt(document.body.style.top || '0') * -1;
                            document.body.classList.remove('notification-open');
                            document.body.style.top = '';
                            window.scrollTo(0, scrollY);
                          }}
                          style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            borderRadius: '8px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                        >
                          <i className="fa-solid fa-times" style={{ color: 'white', fontSize: '14px' }}></i>
                        </button>
                      )}
                    </div>

                    {/* Notifications List */}
                    <div style={{ 
                      maxHeight: isMobile ? '60vh' : '480px', 
                      overflowY: 'auto',
                      background: '#FDF9F2'
                    }}>
                      {/* New orders: each opens its order to add the parcel service and tracking details */}
                      {orderNotifications.length > 0 && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: isMobile ? '8px 16px' : '10px 20px',
                          background: 'var(--gs, #E1EFE3)',
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: 600,
                          color: 'var(--g, #1E5B3A)'
                        }}>
                          <span>
                            New orders{unreadOrderCount > orderNotifications.length ? ` (${unreadOrderCount})` : ''}
                          </span>
                          <button
                            type="button"
                            onClick={markAllOrdersRead}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              color: 'var(--g, #1E5B3A)',
                              fontSize: 'inherit',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Mark all as read
                          </button>
                        </div>
                      )}
                      {orderNotifications.map((notification) => (
                        <div key={notification._id} className="notification-item" data-testid="order-notification" style={{
                          padding: isMobile ? '12px 16px' : '16px 20px',
                          background: 'white',
                          marginBottom: '1px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderLeft: '4px solid var(--g2, #2E7D4F)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--gs, #E1EFE3)';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                        onClick={() => openOrderNotification(notification)}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? '10px' : '14px' }}>
                            <div style={{
                              width: isMobile ? '36px' : '44px',
                              height: isMobile ? '36px' : '44px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #2E7D4F 0%, #1E5B3A 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              boxShadow: '0 4px 12px rgba(40, 167, 69, 0.25)'
                            }}>
                              <i className="fa-solid fa-shopping-cart" style={{ 
                                color: 'white', 
                                fontSize: isMobile ? '16px' : '20px'
                              }}></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                color: '#1a1a1a', 
                                fontSize: isMobile ? '13px' : '14px',
                                marginBottom: '4px'
                              }}>
                                {notification.title}
                              </div>
                              <div style={{ 
                                color: '#495057', 
                                fontSize: isMobile ? '12px' : '13px',
                                lineHeight: '1.4',
                                marginBottom: '8px'
                              }}>
                                {notification.message}
                              </div>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: isMobile ? '4px' : '6px',
                                padding: isMobile ? '3px 8px' : '4px 12px',
                                background: 'var(--gs, #E1EFE3)',
                                color: 'var(--g, #1E5B3A)',
                                borderRadius: '20px',
                                fontSize: isMobile ? '10px' : '12px',
                                fontWeight: 600
                              }}>
                                <i className="fa-solid fa-truck" style={{ fontSize: isMobile ? '9px' : '10px' }}></i>
                                Add tracking details
                                {notification.created_date && (
                                  <span style={{ fontWeight: 400, color: '#6c757d' }}>
                                    · {timeAgo(notification.created_date)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Critical Stock Alerts - Show each product */}
                      {stockAlerts.filter(a => a.severity === 'critical').map((alert, index) => (
                        <div key={alert.id} className="notification-item" style={{
                          padding: isMobile ? '12px 16px' : '16px 20px',
                          background: 'white',
                          marginBottom: '1px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderLeft: '4px solid #dc3545'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fff5f5';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                        onClick={() => {
                          setShowProfileNotification(false);
                          if (isMobile) {
                            // Restore scroll position when navigating
                            const scrollY = parseInt(document.body.style.top || '0') * -1;
                            document.body.classList.remove('notification-open');
                            document.body.style.top = '';
                            window.scrollTo(0, scrollY);
                          }
                          navigate('/productmanagement');
                        }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? '10px' : '14px' }}>
                            <div style={{
                              width: isMobile ? '36px' : '44px',
                              height: isMobile ? '36px' : '44px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #ff4757 0%, #dc3545 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              boxShadow: '0 4px 12px rgba(220, 53, 69, 0.25)'
                            }}>
                              <i className="fa-solid fa-exclamation-triangle" style={{ 
                                color: 'white', 
                                fontSize: isMobile ? '16px' : '20px'
                              }}></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                color: '#1a1a1a', 
                                fontSize: isMobile ? '13px' : '14px',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {alert.productName}
                              </div>
                              <div style={{ 
                                color: '#dc3545', 
                                fontSize: isMobile ? '12px' : '13px',
                                lineHeight: '1.4',
                                marginBottom: '8px',
                                fontWeight: 500
                              }}>
                                {alert.type === 'out_of_stock' 
                                  ? '⚠️ Out of stock' 
                                  : `⚠️ Only ${alert.available} left in stock`}
                              </div>
                              <div style={{
                                display: 'flex',
                                gap: isMobile ? '6px' : '10px',
                                fontSize: isMobile ? '10px' : '11px',
                                color: '#6c757d',
                                background: '#f8f9fa',
                                padding: isMobile ? '4px 8px' : '6px 10px',
                                borderRadius: '6px',
                                flexWrap: isMobile ? 'wrap' : 'nowrap'
                              }}>
                                <span style={{ fontWeight: 500 }}>Total: <strong>{alert.quantity}</strong></span>
                                <span style={{ color: '#dee2e6' }}>|</span>
                                <span style={{ fontWeight: 500 }}>Sold: <strong>{alert.soldQuantity}</strong></span>
                                <span style={{ color: '#dee2e6' }}>|</span>
                                <span style={{ color: '#dc3545', fontWeight: 600 }}>
                                  Available: <strong>{alert.available}</strong>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Low Stock Warnings - Show each product */}
                      {stockAlerts.filter(a => a.severity === 'warning').map((alert, index) => (
                        <div key={alert.id} className="notification-item" style={{
                          padding: isMobile ? '12px 16px' : '16px 20px',
                          background: 'white',
                          marginBottom: '1px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderLeft: '4px solid #ffc107'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fffbf0';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                        onClick={() => {
                          setShowProfileNotification(false);
                          if (isMobile) {
                            // Restore scroll position when navigating
                            const scrollY = parseInt(document.body.style.top || '0') * -1;
                            document.body.classList.remove('notification-open');
                            document.body.style.top = '';
                            window.scrollTo(0, scrollY);
                          }
                          navigate('/productmanagement');
                        }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? '10px' : '14px' }}>
                            <div style={{
                              width: isMobile ? '36px' : '44px',
                              height: isMobile ? '36px' : '44px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #F0854A 0%, #D9640E 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              boxShadow: '0 4px 12px rgba(255, 152, 0, 0.25)'
                            }}>
                              <i className="fa-solid fa-box" style={{ 
                                color: 'white', 
                                fontSize: isMobile ? '16px' : '20px'
                              }}></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                color: '#1a1a1a', 
                                fontSize: isMobile ? '13px' : '14px',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {alert.productName}
                              </div>
                              <div style={{ 
                                color: '#ff9800', 
                                fontSize: isMobile ? '12px' : '13px',
                                lineHeight: '1.4',
                                marginBottom: '8px',
                                fontWeight: 500
                              }}>
                                📦 Low stock - {alert.available} remaining
                              </div>
                              <div style={{
                                display: 'flex',
                                gap: isMobile ? '6px' : '10px',
                                fontSize: isMobile ? '10px' : '11px',
                                color: '#6c757d',
                                background: '#f8f9fa',
                                padding: isMobile ? '4px 8px' : '6px 10px',
                                borderRadius: '6px',
                                flexWrap: isMobile ? 'wrap' : 'nowrap'
                              }}>
                                <span style={{ fontWeight: 500 }}>Total: <strong>{alert.quantity}</strong></span>
                                <span style={{ color: '#dee2e6' }}>|</span>
                                <span style={{ fontWeight: 500 }}>Sold: <strong>{alert.soldQuantity}</strong></span>
                                <span style={{ color: '#dee2e6' }}>|</span>
                                <span style={{ color: '#ff9800', fontWeight: 600 }}>
                                  Available: <strong>{alert.available}</strong>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Profile Incomplete Notification */}
                      {!profileComplete && (
                        <div className="notification-item" style={{
                          padding: isMobile ? '12px 16px' : '16px 20px',
                          background: 'white',
                          marginBottom: '1px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderLeft: '4px solid #ffc107'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fffbf0';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                        onClick={() => {
                          setShowProfileNotification(false);
                          if (isMobile) {
                            // Restore scroll position when navigating
                            const scrollY = parseInt(document.body.style.top || '0') * -1;
                            document.body.classList.remove('notification-open');
                            document.body.style.top = '';
                            window.scrollTo(0, scrollY);
                          }
                          navigate('/profile');
                        }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? '10px' : '14px' }}>
                            <div style={{
                              width: isMobile ? '36px' : '44px',
                              height: isMobile ? '36px' : '44px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #F0854A 0%, #D9640E 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              boxShadow: '0 4px 12px rgba(255, 152, 0, 0.25)'
                            }}>
                              <i className="fa-solid fa-user-edit" style={{ 
                                color: 'white', 
                                fontSize: isMobile ? '16px' : '20px'
                              }}></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                color: '#1a1a1a', 
                                fontSize: isMobile ? '13px' : '14px',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                Complete Your Profile
                              </div>
                              <div style={{ 
                                color: '#6c757d', 
                                fontSize: isMobile ? '12px' : '13px',
                                lineHeight: '1.4',
                                marginBottom: '8px'
                              }}>
                                Complete your profile to unlock all features
                              </div>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: isMobile ? '4px' : '6px',
                                padding: isMobile ? '3px 8px' : '4px 12px',
                                background: '#fff3cd',
                                color: '#856404',
                                borderRadius: '20px',
                                fontSize: isMobile ? '10px' : '12px',
                                fontWeight: 600
                              }}>
                                <i className="fa-solid fa-circle" style={{ fontSize: '6px' }}></i>
                                Action Required
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* No Notifications State */}
                      {profileComplete && criticalStockCount === 0 && lowStockCount === 0 && unreadOrderCount === 0 && (
                        <div style={{ 
                          padding: isMobile ? '40px 16px' : '60px 20px',
                          textAlign: 'center',
                          background: 'white'
                        }}>
                          <div style={{
                            width: isMobile ? '60px' : '80px',
                            height: isMobile ? '60px' : '80px',
                            borderRadius: '50%',
                            background: 'var(--gs, #E1EFE3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                          }}>
                            <i className="fa-solid fa-check-circle" style={{
                              fontSize: isMobile ? '30px' : '40px',
                              color: 'var(--g, #1E5B3A)'
                            }}></i>
                          </div>
                          <p style={{ 
                            margin: '0 0 8px 0', 
                            color: '#1a1a1a', 
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: 600
                          }}>
                            All Caught Up!
                          </p>
                          <p style={{ 
                            margin: 0, 
                            color: '#6c757d', 
                            fontSize: isMobile ? '12px' : '13px',
                            lineHeight: '1.5'
                          }}>
                            You have no new notifications.<br/>We'll let you know when something arrives.
                          </p>
                        </div>
                      )}
                    </div>
                    </div>
                  </>
                )}
              </div>

              {/* CSS Animations */}
              <style>{`
                @keyframes bellShake {
                  0%, 100% { transform: rotate(0deg); }
                  10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
                  20%, 40%, 60%, 80% { transform: rotate(10deg); }
                }
                @keyframes pulse {
                  0%, 100% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.2); opacity: 0.8; }
                }
                
                /* Mobile-specific styles for notification dropdown */
                @media (max-width: 768px) {
                  .notification-dropdown {
                    position: fixed !important;
                    top: 60px !important;
                    left: 10px !important;
                    right: 10px !important;
                    width: calc(100vw - 20px) !important;
                    max-width: none !important;
                    min-width: auto !important;
                    margin-top: 0 !important;
                    z-index: 1070 !important;
                  }
                }
                
                /* Touch-friendly hover effects for mobile */
                @media (hover: none) and (pointer: coarse) {
                  .notification-item:hover {
                    background: white !important;
                    transform: none !important;
                  }
                  
                  .notification-item:active {
                    background: #f8f9fa !important;
                    transform: scale(0.98) !important;
                    transition: all 0.1s ease !important;
                  }
                  
                  .notification-dropdown {
                    -webkit-overflow-scrolling: touch;
                  }
                }
                
                /* Prevent body scroll when notification dropdown is open on mobile */
                @media (max-width: 768px) {
                  body.notification-open {
                    overflow: hidden;
                    position: fixed;
                    width: 100%;
                  }
                }
              `}</style>
            </li>
          )}

          <li className="nav-item dropdown">
            <a className="nav-link  p-1" data-toggle="dropdown" href="#">
              <div className="user-panel d-flex">
                <div className="image user_div">
                  <span className="usercharacter">
                    {email ? email.charAt(0).toUpperCase() : ""}
                  </span>
                </div>
                <div className="info">
                  <span
                    className="d-block f_black"
                    onMouseOver={handleMouseOver}
                    onMouseOut={handleMouseOut}
                    title={email}
                  >
                    {handleProfile(email)}{" "}
                    <i className="fa-solid fa-angle-down" />
                  </span>
                </div>
              </div>
            </a>
            <div className="dropdown-menu dropdown-menu-right">
              <Link to="/profile" className="dropdown-item">
                <i className="fa-solid fa-user mr-2" />
                {t('navigation.myProfile')}
              </Link>
              <div className="dropdown-divider"></div>
              <a href="#" className="dropdown-item" onClick={logout}>
                <i className="fa-solid fa-arrow-right-from-bracket mr-2" />
                {t('navigation.logout')}
              </a>
            </div>
          </li>
        </ul>
      </nav>
      {/* /.navbar */}
      <ShippingBanner status={shipping} businessId={businessId} />

      {/* Main Sidebar Container */}
      <aside
        ref={sidebarRef}
        className="main-sidebar sidebar-light-primary"
      >

        {/* Brand Logo */}
        <a href="#" className="brand-link logo-switch">
          <img
            src="assets/img/logo_sm.png"
            alt="PPEX"
            className="brand-image-xl logo-xs"
          />
          <img
            src="assets/img/logo.png"
            alt="PPEX"
            className="brand-image-xs logo-xl"
            style={{ left: 30 }}
          />
        </a>
        {/* Sidebar */}
        <div className="sidebar">
          {/* Sidebar Menu */}
          <nav className="mt-2">
            <ul
              className="nav nav-pills nav-sidebar flex-column"
              data-widget="treeview"
              role="menu"
              data-accordion="false"
            >
              {/* Add icons to the links using the .nav-icon class
         with font-awesome or any other icon font library */}

              {/* <li className="nav-item">
                <a
                  href="/list"
                  className={`nav-link ${path === "/list" || path === "/products" ? "active" : ""
                    }`}
                >
                  <i className="nav-icon fa-solid fa-box-open" />
                  <p>Products</p>
                </a>
              </li> */}
              {signup === "admin" ? <li className="nav-item">
                <a
                  href="/businesses"
                  className={`nav-link ${path.startsWith("/businesses") ? "active" : ""
                    }`}
                >
                  <i className="nav-icon fa-solid fas fa-briefcase" />
                  <p>{t('navigation.businesses')}</p>
                </a>
              </li> : ""}

              {/* Plan Button - Yellow when Active, Green when Inactive */}
              {/* Only show if user doesn't have active auto-renew subscription */}
              {signup === "user" && showManualPayButton && 
               !hasAutoRenew && (
                <>
                  <style>{`
                    @keyframes shimmer {
                      0% {
                        background-position: -200% center;
                      }
                      100% {
                        background-position: 200% center;
                      }
                    }
                    .plan-upgrade-btn {
                      position: relative;
                      overflow: hidden;
                    }
                    .plan-upgrade-btn::before {
                      content: '';
                      position: absolute;
                      top: 0;
                      left: -100%;
                      width: 100%;
                      height: 100%;
                      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
                      animation: shimmer 2.5s infinite;
                    }
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                    @media (max-width: 768px) {
                      .nav-link p {
                        font-size: 0.9em !important;
                      }
                      .nav-link .badge {
                        font-size: 0.7em !important;
                        padding: 2px 6px !important;
                      }
                    }
                  `}</style>
                  <li className="nav-item">
                    {loadingPlan ? (
                      <a 
                        className={`nav-link ${path.startsWith("/purchase-plan") ? "active" : ""}`}
                        style={{
                          background: 'var(--gs, #E1EFE3)',
                          color: 'var(--g, #1E5B3A)',
                          fontWeight: '700',
                          margin: '8px -1px',
                          padding: '12px 10px',
                          borderRadius: '8px',
                          border: '2px solid var(--g, #1E5B3A)',
                          cursor: 'wait'
                        }}
                      >
                        <i className="nav-icon fa-solid fa-spinner" style={{ 
                          color: 'var(--g, #1E5B3A)',
                          animation: 'spin 1s linear infinite'
                        }}></i>
                        <p style={{ margin: 0 }}>Loading...</p>
                      </a>
                    ) : (
                      <Link to="/purchase-plan">
                        <a 
                          className={`nav-link plan-upgrade-btn ${path.startsWith("/purchase-plan") ? "active" : ""}`}
                        style={{
                          background: hasManualPlan
                            ? 'var(--os, #FCE3C6)'
                            : 'var(--g, #1E5B3A)',
                          backgroundSize: '200% 100%',
                          color: hasManualPlan
                            ? 'var(--oh, #9C4308)'
                            : 'white',
                          fontWeight: '700',
                          margin: '8px -2px',
                          padding: '12px 10px',
                          borderRadius: '8px',
                          border: hasManualPlan
                            ? '2px solid #F3C99A'
                            : '2px solid var(--g, #1E5B3A)',
                          boxShadow: hasManualPlan
                            ? '0 6px 16px -8px rgba(217, 100, 14, 0.45)'
                            : '0 6px 16px -8px rgba(30, 91, 58, 0.55)',
                          position: 'relative',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateX(3px)';
                          e.currentTarget.style.boxShadow = hasManualPlan
                            ? '0 10px 22px -8px rgba(217, 100, 14, 0.55)'
                            : '0 10px 22px -8px rgba(30, 91, 58, 0.65)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateX(0)';
                          e.currentTarget.style.boxShadow = hasManualPlan
                            ? '0 6px 16px -8px rgba(217, 100, 14, 0.45)'
                            : '0 6px 16px -8px rgba(30, 91, 58, 0.55)';
                        }}
                      >
                        {/* Collapsed sidebar only (AdminTheme.css): ✓ plan purchased, ! not purchased */}
                        <i
                          aria-hidden="true"
                          className={`plan-mini-icon fa-solid ${hasManualPlan ? 'fa-check' : 'fa-exclamation'}`}
                        />
                        <p style={{
                          textShadow: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          margin: 0
                        }}>
                          <span>{t('manualpay')}</span>
                          <span className="badge" style={{ 
                            background: hasManualPlan
                              ? 'rgba(217, 100, 14, 0.14)'
                              : 'rgba(255, 255, 255, 0.3)',
                            color: hasManualPlan
                              ? 'var(--oh, #9C4308)'
                              : 'white',
                            fontWeight: '700',
                            padding: '4px 8px',
                            borderRadius: '10px',
                            border: hasManualPlan
                              ? '1px solid rgba(156, 67, 8, 0.25)'
                              : '1px solid rgba(255, 255, 255, 0.5)',
                            minWidth: '70px',
                            textAlign: 'center',
                            display: 'inline-block'
                          }}>
                            {(() => {
                              // Check if manual plan is active
                              const isManualActive = hasManualPlan;
                              
                              // Check if subscription is active
                              const isSubscriptionActive = (subscriptionStatus?.status === 'active' || 
                                subscriptionStatus?.status === 'authenticated') && 
                                subscriptionStatus?.paid_count > 0;
                              
                              // Check if user is in free trial
                              const isFreeTrial = paymentType === 'free_trial';
                              
                              // If in free trial, show TRIAL
                              if (isFreeTrial) {
                                return 'TRIAL';
                              }
                              
                              // If both are inactive and not in trial, show EXPIRED
                              if (!isManualActive && !isSubscriptionActive) {
                                return 'INACTIVE';
                              }
                              
                              // Otherwise show ACTIVE or INACTIVE based on manual plan
                              return isManualActive ? 'ACTIVE' : 'INACTIVE';
                            })()}
                          </span>
                        </p>
                      </a>
                    </Link>
                    )}
                  </li>
                </>
              )}

              {/* Subscription Button - Yellow when Active, Green when Inactive */}
              {signup === "user" && !hasManualPlan && (
                <li className="nav-item">
                  {loadingPlan ? (
                    <a 
                      className={`nav-link ${path.startsWith("/subscription") ? "active" : ""}`}
                      style={{
                        background: 'var(--gs, #E1EFE3)',
                        color: 'var(--g, #1E5B3A)',
                        fontWeight: '700',
                        margin: '8px -1px',
                        padding: '12px 10px',
                        borderRadius: '8px',
                        border: '2px solid var(--g, #1E5B3A)',
                        cursor: 'wait'
                      }}
                    >
                      <i className="nav-icon fa-solid fa-spinner" style={{ 
                        color: 'var(--g, #1E5B3A)',
                        animation: 'spin 1s linear infinite'
                      }}></i>
                      <p style={{ margin: 0 }}>Loading...</p>
                    </a>
                  ) : (
                    <Link to="/subscription">
                      <a 
                        className={`nav-link ${path.startsWith("/subscription") ? "active" : ""}`}
                      style={{
                        background: hasAutoRenew
                          ? 'var(--os, #FCE3C6)' 
                          : 'var(--g, #1E5B3A)',
                        backgroundSize: '200% 100%',
                        color: hasAutoRenew
                          ? 'var(--oh, #9C4308)'
                          : 'white',
                        fontWeight: '700',
                        margin: '8px -1px',
                        padding: '12px 10px',
                        borderRadius: '8px',
                        border: hasAutoRenew
                          ? '2px solid #F3C99A' 
                          : '2px solid var(--g, #1E5B3A)',
                        boxShadow: hasAutoRenew
                          ? '0 6px 16px -8px rgba(217, 100, 14, 0.45)' 
                          : '0 6px 16px -8px rgba(30, 91, 58, 0.55)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(3px)';
                        e.currentTarget.style.boxShadow = hasAutoRenew
                          ? '0 10px 22px -8px rgba(217, 100, 14, 0.55)' 
                          : '0 10px 22px -8px rgba(30, 91, 58, 0.65)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = hasAutoRenew
                          ? '0 6px 16px -8px rgba(217, 100, 14, 0.45)' 
                          : '0 6px 16px -8px rgba(30, 91, 58, 0.55)';
                      }}
                    >
                      {/* Collapsed sidebar only (AdminTheme.css): ✓ plan purchased, ! not purchased */}
                      <i
                        aria-hidden="true"
                        className={`plan-mini-icon fa-solid ${hasAutoRenew ? 'fa-check' : 'fa-exclamation'}`}
                      />
                      <p style={{
                        textShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        margin: 0
                      }}>
                        <span>{t('Auto-Renew')}</span>
                        <span className="badge" style={{ 
                          background: hasAutoRenew
                            ? 'rgba(217, 100, 14, 0.14)'
                            : 'rgba(255, 255, 255, 0.3)',
                          color: hasAutoRenew
                            ? 'var(--oh, #9C4308)'
                            : 'white',
                          fontWeight: '700',
                          padding: '4px 8px',
                          borderRadius: '10px',
                          border: hasAutoRenew
                            ? '1px solid rgba(156, 67, 8, 0.25)'
                            : '1px solid rgba(255, 255, 255, 0.5)',
                          minWidth: '70px',
                          textAlign: 'center',
                          display: 'inline-block'
                        }}>
                          {(() => {
                            // Check if subscription is active (includes discount subscriptions but excludes cancelled)
                            const isSubscriptionActive = hasAutoRenew;
                            
                            // Check if manual plan is active
                            const isManualActive = hasManualPlan;
                            
                            // Check if user is in free trial
                            const isFreeTrial = paymentType === 'free_trial';
                            
                            // If in free trial, show TRIAL
                            if (isFreeTrial) {
                              return 'TRIAL';
                            }
                            
                            // If both are inactive and not in trial, show EXPIRED
                            if (!isSubscriptionActive && !isManualActive) {
                              return 'INACTIVE';
                            }
                            
                            // Otherwise show ACTIVE or INACTIVE based on subscription
                            return isSubscriptionActive ? 'ACTIVE' : 'INACTIVE';
                          })()}
                        </span>
                      </p>
                    </a>
                  </Link>
                  )}
                </li>
              )}

              <li className="nav-item">
                <Link to={'/users'}>
                  <a
                    className={`nav-link ${path.startsWith("/user") ? "active" : ""
                      }`}
                  >
                    <i className="nav-icon fa-solid fa-users" />
                    <p>{t('navigation.users')}</p>
                  </a>
                </Link>
              </li>

              <li className="nav-item">
                <Link to={'/gift'}>
                  <a
                    className={`nav-link ${path.startsWith("/gift") ? "active" : ""
                      }`}
                  >
                    <i className="nav-icon fa-solid fa-boxes-stacked" />
                    <p>{t('productTab.title')}</p>
                  </a>
                </Link>

              </li>
              
              {/* E-Books Management - Show only for business users */}
              {signup === "user" && (
                <li className="nav-item">
                  <Link to={'/ebooks'}>
                    <a
                      className={`nav-link ${path.startsWith("/ebooks") ? "active" : ""}`}
                    >
                      <i className="nav-icon fa-solid fa-book" />
                      <p>E-Books</p>
                    </a>
                  </Link>
                </li>
              )}
              
              {/* Analytics Hub - Show only for business users */}
              {signup === "user" && (
                <li className="nav-item">
                  <Link to={'/analytics-hub'}>
                    <a
                      className={`nav-link ${path.startsWith("/analytics") || path.startsWith("/meta") ? "active" : ""}`}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                        <line x1="18" y1="20" x2="18" y2="10"></line>
                        <line x1="12" y1="20" x2="12" y2="4"></line>
                        <line x1="6" y1="20" x2="6" y2="14"></line>
                      </svg>
                      <p>Analytics</p>
                    </a>
                  </Link>
                </li>
              )}
              
              <li className="nav-item">
                <Link to={'/category'}>
                  <a
                    className={`nav-link ${path.startsWith("/category") ? "active" : ""
                      }`}
                  >
                    <i className="nav-icon fa-solid fa-tags" />
                    <p>{t('categoryTab.title')}</p>

                  </a>
                </Link>

              </li>
              <li className="nav-item">
                <Link to={'/ordermanagement'}>
                  <a
                    className={`nav-link ${path.startsWith("/ordermanagement") ? "active" : ""
                      }`}
                  >
                    <i className="nav-icon fa-solid fa-box" />
                    <p>{t('navigation.orderManagement')}</p>
                  </a>
                </Link>

              </li>
              <li className="nav-item">
                <Link to={'/gst-management'}>
                  <a
                    className={`nav-link ${path.startsWith("/gst-management") ? "active" : ""
                      }`}
                  >
                    <i className="nav-icon fa-solid fa-file-invoice-dollar" />
                    <p>{t('navigation.gst_management')}</p>
                  </a>
                </Link>

              </li>
              
              <li className="nav-item">
                <Link to={'/productmanagement'}>
                  <a
                    className={`nav-link ${path.startsWith("/productmanagement") ? "active" : ""
                      }`}
                  >
                    <i className="nav-icon fa-solid fa-warehouse" />
                    <p>{t('navigation.productinventoryhub')}</p>
                  </a>
                </Link>

              </li>

              {signup === "user" && businessData && (
                <>
                  {!businessData.activation_status ? (
                    <li className="nav-item">
                      <Link to={'/create-payment'}>
                        <a
                          className={`nav-link ${path.startsWith("/create-payment") ? "active" : ""}`}
                        >
                          <i className="nav-icon fa-solid fa-plus-circle" />
                          <p>{t('navigation.createPayment')}</p>
                        </a>
                      </Link>

                    </li>
                  ) : (
                    <li className="nav-item">
                      <Link to={'/payment'}>
                        <a
                          className={`nav-link ${path.startsWith("/payment") ? "active" : ""}`}
                        >

                          <i className="nav-icon fa-solid fa-credit-card" />
                          <p>
                            {t('navigation.payment')}
                            {/* <span
                              className={`badge badge-sm ml-1  ${businessData.activation_status === "activated"
                                ? "badge-success"
                                : businessData.activation_status === "needs_clarification"
                                  ? "badge-warning"
                                  : "badge-danger"
                                }`}
                            >
                              {businessData.activation_status === "needs_clarification" ? "in active" : businessData.activation_status}
                            </span> */}
                          </p>

                        </a>
                      </Link>

                    </li>
                  )}
                </>
              )}

              {signup === "user" && businessData && (
                <>
                  <li className="nav-header">{t('navigation.webStore')}</li>

                  {/* Show "Create Web Store" only if user has no store */}
                  {!businessData.has_web_store ? (
                    <li className="nav-item">
                      <Link >
                        <a
                          href=""
                          className={`nav-link ${path.startsWith("/webstore/create") ? "active" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            createWebStore();
                          }}
                        >
                          <i className="nav-icon fa-solid fa-plus-circle" />
                          <p>{t('navigation.createWebStore')}</p>
                        </a>
                      </Link>

                    </li>
                  ) : (
                    <>
                      {/* Always show Store Settings if user has a store */}
                      <li className="nav-item">
                        <Link to={'/webstore_settings'}>
                          <a
                            className={`nav-link ${path.startsWith("/webstore_settings") ? "active" : ""}`}
                          >
                            <i className="nav-icon fa-solid fa-cog" />
                            <p>{t('navigation.storeSettings')}</p>
                          </a>
                        </Link>

                      </li>

                      {/* Shipping Settings, with SET UP / FIX while customers can't (or soon can't) order */}
                      <li className="nav-item">
                        {/* The Link is the nav-link itself (no <a> inside a Link) */}
                        <Link
                          to={SHIPPING_SETTINGS_PATH}
                          className={`nav-link ${path.startsWith(SHIPPING_SETTINGS_PATH) ? "active" : ""}`}
                        >
                          <i className="nav-icon fa-solid fa-truck" />
                          <p>
                            {t('shipping.navLink')}
                            <ShippingNavBadge status={shipping} />
                          </p>
                        </Link>
                      </li>

                      {/* Custom Domain Settings */}
                      <li className="nav-item">
                        <Link to={'/domain-settings'}>
                          <a
                            className={`nav-link ${path.startsWith("/domain-settings") ? "active" : ""}`}
                          >
                            <i className="nav-icon fa-solid fa-globe" />
                            <p>Custom Domain</p>
                          </a>
                        </Link>
                      </li>

                      {/* Invoices - Only show if user has purchased a plan */}
                      {hasManualPlan || 
                        (subscriptionStatus?.paid_count > 0 && subscriptionStatus?.status !== 'cancelled') ? (
                        <li className="nav-item">
                          <Link to={'/my-invoices'}>
                            <a
                              className={`nav-link ${path.startsWith("/my-invoices") ? "active" : ""}`}
                            >
                              <i className="nav-icon fa-solid fa-file-invoice" />
                              <p>My Invoices</p>
                            </a>
                          </Link>
                        </li>
                      ) : null}

                      {/* Show View Store only if store exists */}
                      <li className="nav-item">
                        <a
                         href={`https://${businessData.store_subdomain}.mytamilkart.com/?lang=${i18n.language}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="nav-link"
                        >

                          <i className="nav-icon fa-solid fa-external-link-alt" />
                          <p>
                            {t('navigation.viewStore')}
                            <span
                              className={`badge badge-sm ml-1 ${businessData.store_status === "active"
                                ? "badge-success"
                                : businessData.store_status === "inactive"
                                  ? "badge-warning"
                                  : "badge-danger"
                                }`}
                            >
                              {businessData.store_status}
                            </span>
                          </p>

                        </a>


                      </li>
                    </>
                  )}
                </>
              )}


            </ul>
          </nav>
          {/* /.sidebar-menu */}
        </div>
        {/* /.sidebar */}

      </aside>
    </div>
  );
};

export default NavigationBar;
